import type { RuleDefinition, SourceReference } from "./types";
export { cantons } from "./cantons";

const reviewedAt = "2026-07-27";
export const contentPackVersion = "core-2026.07.27";

const source = (
  label: string,
  authority: string,
  url: string,
  jurisdiction: SourceReference["jurisdiction"] = "federal",
  verify = false,
): SourceReference => ({
  label,
  authority,
  url,
  jurisdiction,
  reviewedAt,
  verify,
  reviewIntervalDays: verify ? 90 : 180,
});

const baseRules: RuleDefinition[] = [
  {
    id: "move-registration",
    title: "Close the loop on your move",
    domain: "Residency",
    requirement: "required",
    summary: "Register the new address and update every institution that can send official mail or charge you.",
    why: "In most cases, arrival at the new commune must be registered within 14 days. Address mismatches are a common source of missed letters and billing friction.",
    impact: "Avoid missed deadlines and coverage gaps",
    effort: "45–90 min",
    timing: { type: "event", event: "moving", offsetDays: 14, label: "Within 14 days after moving", confidence: "exact" },
    recurrence: "once",
    applicability: { event: ["moving"] },
    logic: "Shown because you selected moving as your next life event. The due date is 14 days after your event date.",
    documents: ["Identity document", "Lease or proof of address", "Current permit if applicable"],
    steps: [
      { id: "commune", title: "Register departure and arrival with the commune" },
      { id: "employer-bank", title: "Update employer, bank and health insurer" },
      { id: "contracts", title: "Update utilities, internet, subscriptions and vehicle records" },
      { id: "mail", title: "Set up mail forwarding", optional: true },
    ],
    sources: [
      source("Notification of departure and registration", "ch.ch", "https://www.ch.ch/en/housing/moving/notification-of-departure-and-registration/", "federal", true),
      source("eMovingCH", "eOperations Switzerland", "https://www.eumzug.swiss/eumzugngx/global", "communal", true),
    ],
    legacyIds: ["move_admin_prep", "registration_cleanup"],
  },
  {
    id: "permit-renewal",
    title: "Prepare your permit renewal",
    domain: "Residency",
    requirement: "required",
    summary: "Start early, find the authority named by your canton or commune, and record the deadline shown on its notice.",
    why: "Permit-renewal routes and submission timing depend on the responsible local authority. Your permit expiry is a planning anchor, not proof of a universal filing deadline.",
    impact: "Protect continuity of residence and employment",
    effort: "30–60 min",
    timing: { type: "profile-date", field: "permitExpiry", offsetDays: -90, windowOffsetDays: -90, label: "Preparation target — verify the authority deadline", confidence: "review-window" },
    recurrence: "once",
    deadlineKind: "review",
    pastDueBehavior: "verify-now",
    applicability: { residency: ["eu-efta", "other"] },
    logic: "Shown for non-Swiss residents when a permit expiry date is available. CH/OS creates an early preparation target; only an authority notice or official cantonal route establishes the filing deadline.",
    documents: ["Current permit", "Valid passport or ID", "Expiry notice if received"],
    steps: [
      { id: "check-route", title: "Confirm whether the canton or commune handles renewal" },
      { id: "record-deadline", title: "Record the deadline from the authority notice or official route" },
      { id: "documents", title: "Collect permit, ID and expiry notice" },
      { id: "submit", title: "Submit the renewal request" },
      { id: "follow-up", title: "Save confirmation and follow-up date", optional: true },
    ],
    sources: [
      source("Permits for living in Switzerland", "ch.ch", "https://www.ch.ch/en/foreign-nationals-in-switzerland/entry-and-stay-in-switzerland/permits-for-living-in-switzerland/", "federal", true),
    ],
  },
  {
    id: "health-review",
    title: "Run your annual health-insurance review",
    domain: "Health",
    requirement: "recommended",
    summary: "Compare insurer, model, deductible and accident cover, then act early enough for any cancellation to be received by 30 November.",
    why: "Premiums and the cheapest combination can change each year. Basic and supplementary insurance have different rules and should be reviewed separately.",
    impact: "Potentially high recurring savings",
    effort: "30–45 min",
    timing: { type: "annual", month: 11, day: 15, windowStartMonth: 9, label: "Recommended send-by date: 15 November", confidence: "review-window" },
    recurrence: "annual",
    deadlineKind: "planning",
    hardDeadlineMonth: 11,
    hardDeadlineDay: 30,
    hardDeadlineLabel: "Basic-insurance cancellation must be received by 30 November",
    logic: "Shown every autumn. The 15 November action date gives postal margin before the strict 30 November receipt deadline. If you work at least eight hours per week, the accident-cover step highlights possible duplicate cover.",
    documents: ["Current policy", "Expected healthcare usage", "Employer accident-cover confirmation"],
    steps: [
      { id: "priminfo", title: "Compare basic premiums on official Priminfo" },
      { id: "model-franchise", title: "Review model and deductible together" },
      { id: "accident", title: "Confirm whether employer accident cover applies" },
      { id: "switch", title: "If switching, send cancellation by 15 November and enroll with the new insurer in parallel" },
      { id: "supplementary", title: "Review supplementary cover separately", optional: true },
    ],
    sources: [
      source("Official premium calculator", "Federal Office of Public Health", "https://www.priminfo.admin.ch/en/praemien", "federal", true),
      source("Official switching deadlines", "Federal Office of Public Health", "https://www.bag.admin.ch/en/premiums-and-costs-answers-to-frequently-asked-questions"),
      source("Health insurance costs", "ch.ch", "https://www.ch.ch/en/health/health-insurance/health-insurance-costs/"),
      source("Changing health insurance", "ch.ch", "https://www.ch.ch/en/health/health-insurance/changing-health-insurance/", "federal", true),
    ],
    legacyIds: ["health_insurance_review", "accident_cover_check", "supplementary_review"],
  },
  {
    id: "tax-season",
    title: "Prepare your Swiss tax file",
    domain: "Tax",
    requirement: "required",
    summary: "Collect the annual documents, estimate liquidity and confirm your canton’s filing deadline.",
    why: "Tax deadlines and extension processes differ by canton. Preparing the evidence early makes deductions and cash planning more deliberate.",
    impact: "Lower deadline risk and better deduction quality",
    effort: "1–3 hr",
    timing: { type: "annual", month: 3, day: 15, windowStartMonth: 1, label: "January–March; canton deadline varies", confidence: "canton-specific" },
    recurrence: "annual",
    deadlineKind: "review",
    pastDueBehavior: "verify-now",
    logic: "Shown annually. The date is a planning checkpoint, not a universal filing deadline; verify your canton’s portal.",
    documents: ["Salary certificate", "Bank and investment tax statements", "Pension and insurance certificates", "Deductible expense receipts"],
    steps: [
      { id: "salary", title: "Collect salary certificate and pension statements" },
      { id: "bank", title: "Collect bank and investment tax statements" },
      { id: "deductions", title: "Collect deduction evidence" },
      { id: "deadline", title: "Confirm filing or extension deadline with your canton" },
    ],
    sources: [
      source("Tax return overview", "ch.ch", "https://www.ch.ch/en/taxes-and-finances/tax-return/", "federal", true),
      source("Swiss tax calculator", "Federal Tax Administration", "https://swisstaxcalculator.estv.admin.ch/", "federal", true),
      source("Salary certificate", "ch.ch", "https://www.ch.ch/en/work/salary/salary-certificate/"),
    ],
  },
  {
    id: "tax-at-source-check",
    title: "Check your tax-at-source position",
    domain: "Tax",
    requirement: "recommended",
    summary: "Confirm whether an ordinary assessment is required or useful before making deduction-driven decisions.",
    why: "Tax-at-source and ordinary assessment interact with income, permit status, wealth, and deductions. A 3a contribution is not automatically beneficial in every case.",
    impact: "Avoid unsuitable tax assumptions",
    effort: "20–40 min",
    timing: { type: "annual", month: 2, day: 15, windowStartMonth: 1, label: "Early in tax season", confidence: "canton-specific" },
    recurrence: "annual",
    deadlineKind: "review",
    pastDueBehavior: "verify-now",
    applicability: { residency: ["eu-efta", "other"] },
    logic: "Shown for foreign residents. It becomes more prominent when your tax mode is tax-at-source or unsure.",
    documents: ["Permit", "Annual salary", "Wealth and other income overview"],
    steps: [
      { id: "status", title: "Confirm current tax status and assessment route", conceptId: "tax-at-source" },
      { id: "thresholds", title: "Check canton-specific thresholds and request process" },
      { id: "deductions", title: "Model deductions before committing funds", optional: true },
    ],
    sources: [
      source("Tax at source", "ch.ch", "https://www.ch.ch/en/taxes-and-finances/types-of-taxation/tax-at-source/", "federal", true),
    ],
    conceptId: "tax-at-source",
  },
  {
    id: "pillar-stack",
    title: "Set your annual pension rhythm",
    domain: "Pension",
    requirement: "recommended",
    summary: "Plan 3a early, inspect Pillar 2 selectively and keep liquidity in view.",
    why: "Retirement decisions are easier when spread across the year. Pillar 2 buy-ins depend on tax, lock-up and future withdrawal plans.",
    impact: "Long-term tax and retirement leverage",
    effort: "45–90 min",
    timing: { type: "annual", month: 10, day: 31, windowStartMonth: 8, label: "August–October planning checkpoint", confidence: "review-window" },
    recurrence: "annual",
    applicability: { employment: ["employed", "self-employed"] },
    logic: "Shown for people in paid work. Exact contribution limits and personal tax treatment must be checked against current official guidance.",
    documents: ["Pension-fund statement", "3a contribution record", "Tax estimate", "Liquidity plan"],
    steps: [
      { id: "3a", title: "Choose this year’s 3a contribution target", conceptId: "pillar3a" },
      { id: "pillar2", title: "Review Pillar 2 statement and buy-in capacity", conceptId: "pillar2-buyin" },
      { id: "liquidity", title: "Check tax and liquidity before transferring funds" },
      { id: "beneficiaries", title: "Review beneficiary nominations", optional: true },
    ],
    sources: [
      source("The third pillar", "ch.ch", "https://www.ch.ch/en/work/old-age-pension/3rd-pillar/", "federal", true),
      source("Occupational pension", "ch.ch", "https://www.ch.ch/en/work/old-age-pension/occupational-pension/", "federal", true),
    ],
    legacyIds: ["tax_pension_projection"],
  },
  {
    id: "ahv-record",
    title: "Check your AHV contribution record",
    domain: "Pension",
    requirement: "recommended",
    summary: "Order an individual-account statement and investigate missing contribution years.",
    why: "Contribution gaps can reduce the pension for the full duration of entitlement, and the statement is available free of charge.",
    impact: "Protect future pension entitlement",
    effort: "20–30 min",
    timing: { type: "annual", month: 6, day: 15, windowStartMonth: 5, label: "Annual summer check", confidence: "review-window" },
    recurrence: "annual",
    logic: "Shown annually as a lightweight verification. It is especially useful after job changes, time abroad or periods without paid work.",
    documents: ["AHV office history if known", "Employment timeline"],
    steps: [
      { id: "request", title: "Request the individual-account statement" },
      { id: "compare", title: "Compare contribution years with your work history" },
      { id: "resolve", title: "Ask the compensation office about any gap" },
    ],
    sources: [
      source("Statement of the individual account", "OASI/DI Information Centre", "https://www.ahv-iv.ch/en/Forms/Statement-of-the-individual-account"),
    ],
    conceptId: "ahv-gap",
  },
  {
    id: "home-protection",
    title: "Review your home protection",
    domain: "Home",
    requirement: "recommended",
    summary: "Make sure liability, contents and address details match your actual living situation.",
    why: "A move, new valuables or a household change can create gaps or unnecessary cover. Renters should distinguish personal liability from contents insurance.",
    impact: "Reduce high-consequence household risk",
    effort: "20–40 min",
    timing: { type: "annual", month: 8, day: 31, windowStartMonth: 7, label: "Late-summer review", confidence: "review-window" },
    recurrence: "annual",
    logic: "Shown for every household, with renter- or owner-specific wording in the profile.",
    documents: ["Current policies", "Inventory of meaningful valuables", "Lease or mortgage details"],
    steps: [
      { id: "address", title: "Verify insured address and household members" },
      { id: "liability", title: "Review personal liability cover" },
      { id: "contents", title: "Review contents value and meaningful exclusions" },
      { id: "extras", title: "Remove add-ons you would not consciously rebuy", optional: true },
    ],
    sources: [
      source("Personal liability insurance", "ch.ch", "https://www.ch.ch/en/insurance/personal-liability-insurance/"),
      source("Household insurance", "ch.ch", "https://www.ch.ch/en/housing/rent/rental-agreement/household-insurance/"),
    ],
    legacyIds: ["move_insurance_bundle"],
  },
  {
    id: "rent-review",
    title: "Check your rent against the reference rate",
    domain: "Home",
    requirement: "optional",
    summary: "Review whether a reference-rate change or contract change gives you a reason to act.",
    why: "Swiss rent adjustments can be tied to the mortgage reference rate, but the calculation depends on the lease and prior adjustments.",
    impact: "Possible recurring housing savings",
    effort: "20–45 min",
    timing: { type: "annual", month: 6, day: 5, windowStartMonth: 3, label: "Check after official rate updates", confidence: "review-window" },
    recurrence: "annual",
    applicability: { housing: ["renter"] },
    logic: "Shown only to renters. It is a review prompt, not an automatic claim that your rent should change.",
    documents: ["Lease", "Last rent adjustment letter", "Current reference rate"],
    steps: [
      { id: "rate", title: "Check the current official reference rate" },
      { id: "lease", title: "Compare it with the basis in your lease or last adjustment" },
      { id: "calculate", title: "Use a tenant-association calculator or seek advice", optional: true },
    ],
    sources: [
      source("Tenancy and rent information", "ch.ch", "https://www.ch.ch/en/housing/rent/", "federal", true),
      source("Mortgage reference interest rate", "Federal Housing Office", "https://www.bwo.admin.ch/de/referenzzinssatz", "federal", true),
    ],
  },
  {
    id: "work-cover",
    title: "Know what work already covers",
    domain: "Work",
    requirement: "recommended",
    summary: "Map accident, sickness, pension and other employer benefits before buying overlapping protection.",
    why: "Employees working at least eight hours per week are generally covered for non-occupational accidents through their employer.",
    impact: "Avoid duplicate cover and blind spots",
    effort: "30 min",
    timing: { type: "annual", month: 9, day: 15, windowStartMonth: 8, label: "Before insurance season", confidence: "review-window" },
    recurrence: "annual",
    applicability: { employment: ["employed"] },
    logic: "Shown for employees. Accident-cover guidance adapts to the weekly-hours field.",
    documents: ["Employment contract", "Benefits summary", "Pension-fund certificate"],
    steps: [
      { id: "accident", title: "Confirm occupational and non-occupational accident cover" },
      { id: "sickness", title: "Review salary continuation and sickness benefits" },
      { id: "pension", title: "Read the employer pension-fund statement" },
      { id: "extras", title: "List other employer benefits", optional: true },
    ],
    sources: [
      source("Accident insurance", "ch.ch", "https://www.ch.ch/en/work/illness--accident--disability/accident-insurance"),
      source("Absence due to illness or accident", "ch.ch", "https://www.ch.ch/en/work/illness--accident--disability/absences-from-work-due-to-illness-or-accident/"),
    ],
  },
  {
    id: "cash-buffer",
    title: "Stress-test your cash buffer",
    domain: "Money",
    requirement: "recommended",
    summary: "Keep enough accessible cash for taxes, insurance resets and life changes before optimizing harder.",
    why: "A healthy buffer reduces forced selling and turns high income into resilience rather than lifestyle drift.",
    impact: "Higher financial resilience",
    effort: "20–30 min",
    timing: { type: "annual", month: 7, day: 15, windowStartMonth: 6, label: "Mid-year review", confidence: "review-window" },
    recurrence: "annual",
    logic: "Shown to everyone. The app uses your optional buffer-in-months answer only as context and never assumes invested assets are emergency cash.",
    documents: ["Core monthly-spend estimate", "Tax reserve", "Upcoming large expenses"],
    steps: [
      { id: "core-spend", title: "Estimate essential monthly spending" },
      { id: "target", title: "Choose a realistic buffer target" },
      { id: "gaps", title: "Reserve for taxes and known near-term costs" },
      { id: "surplus", title: "Define what counts as true investable surplus", optional: true },
    ],
    sources: [
      source("Taxes and finances", "ch.ch", "https://www.ch.ch/en/taxes-and-finances/"),
    ],
    conceptId: "cash-buffer",
    legacyIds: ["cash_buffer_review"],
  },
  {
    id: "mobility-review",
    title: "Run your mobility admin check",
    domain: "Mobility",
    requirement: "recommended",
    summary: "Review registration, insurance, inspection posture and motorway access for vehicles you actually use.",
    why: "Coverage, storage and usage can change during the year, especially after a move or vehicle purchase.",
    impact: "Reduce renewal and compliance surprises",
    effort: "20–40 min",
    timing: { type: "annual", month: 1, day: 20, windowStartMonth: 12, label: "December–January", confidence: "review-window" },
    recurrence: "annual",
    applicability: { hasVehicle: true },
    logic: "Shown only when a car or motorbike is present in the profile.",
    documents: ["Registration document", "Insurance policy", "Inspection correspondence"],
    steps: [
      { id: "registration", title: "Confirm registration address and documents" },
      { id: "cover", title: "Check insurance against vehicle value and usage" },
      { id: "inspection", title: "Review inspection or maintenance timing", conceptId: "mfk" },
      { id: "vignette", title: "Confirm motorway vignette if needed", optional: true },
    ],
    sources: [
      source("Vehicles and traffic", "ch.ch", "https://www.ch.ch/en/vehicles-and-traffic/"),
      source("Motorway charge", "Federal Office for Customs and Border Security", "https://www.bazg.admin.ch/en/motorway-charge-vignette", "federal", true),
    ],
    legacyIds: ["motorbike_review"],
  },
  {
    id: "protection-kit",
    title: "Build your personal protection kit",
    domain: "Protection",
    requirement: "recommended",
    summary: "Create a resilient document, access and emergency setup without storing sensitive documents in CH/OS.",
    why: "Good organization reduces friction during an accident, job change, loss of access or family emergency.",
    impact: "Lower recovery time during stressful events",
    effort: "45–90 min",
    timing: { type: "annual", month: 11, day: 10, windowStartMonth: 10, label: "October–November", confidence: "review-window" },
    recurrence: "annual",
    logic: "Shown annually. CH/OS tracks completion only; it never asks you to upload identity, policy or banking documents.",
    documents: ["Document inventory only — do not upload documents here"],
    steps: [
      { id: "inventory", title: "List where essential documents are stored" },
      { id: "backup", title: "Verify encrypted backup and recovery access" },
      { id: "2fa", title: "Check password manager and two-factor recovery" },
      { id: "legal", title: "Review emergency contacts and basic legal documents", optional: true },
    ],
    sources: [
      source("Online access to public administration", "ch.ch", "https://www.ch.ch/en/safety-and-justice/addresses-of-administrative-authorities"),
    ],
    legacyIds: ["legal_docs_review"],
  },
  {
    id: "contract-sweep",
    title: "Sweep recurring contracts",
    domain: "Money",
    requirement: "optional",
    summary: "Review subscriptions, telecom, memberships and annual renewals in one focused session.",
    why: "Small commitments quietly consume both money and attention, especially after a move.",
    impact: "Quick recurring savings",
    effort: "30–45 min",
    timing: { type: "annual", month: 11, day: 30, windowStartMonth: 10, label: "October–November", confidence: "review-window" },
    recurrence: "annual",
    logic: "Shown every year as a cleanup prompt. It does not connect to financial accounts.",
    documents: ["Bank or card transaction overview", "Contract notice periods"],
    steps: [
      { id: "scan", title: "Scan recurring payments and annual invoices" },
      { id: "rebuy", title: "Flag anything you would not buy again today" },
      { id: "notice", title: "Check notice periods before cancelling" },
    ],
    sources: [
      source("Swiss public information portal", "ch.ch", "https://www.ch.ch/en/"),
    ],
    legacyIds: ["renewals_subscriptions"],
  },
  {
    id: "settle-belong",
    title: "Invest in feeling local",
    domain: "Belonging",
    requirement: "optional",
    summary: "Turn language, administration and social connection into a small, deliberate annual plan.",
    why: "Expats consistently report that settling in and making local connections are harder than Switzerland’s quality of life would suggest.",
    impact: "Higher day-to-day confidence and belonging",
    effort: "Choose your own pace",
    timing: { type: "annual", month: 4, day: 15, windowStartMonth: 2, label: "Spring reset", confidence: "review-window" },
    recurrence: "annual",
    applicability: { residency: ["eu-efta", "other"], maxYearsSinceArrival: 5 },
    logic: "Shown to people within roughly five years of arriving in Switzerland. It can be dismissed permanently for the current year.",
    documents: [],
    steps: [
      { id: "language", title: "Choose one practical local-language goal" },
      { id: "local-service", title: "Set up the local digital administration services you use" },
      { id: "community", title: "Join one recurring local group or activity", optional: true },
      { id: "explore", title: "Plan one new Swiss place or tradition", optional: true },
    ],
    sources: [
      source("Integration for foreign nationals", "ch.ch", "https://www.ch.ch/en/foreign-nationals-in-switzerland/living-in-switzerland/integration-in-switzerland/"),
    ],
  },
  {
    id: "annual-reset",
    title: "Run your CH/OS annual reset",
    domain: "Protection",
    requirement: "recommended",
    summary: "Close the year, archive what matters and set next year’s key dates.",
    why: "A repeatable annual rhythm prevents Swiss administration from becoming a stream of isolated surprises.",
    impact: "A calmer next year",
    effort: "45–60 min",
    timing: { type: "annual", month: 12, day: 15, windowStartMonth: 12, label: "December", confidence: "review-window" },
    recurrence: "annual",
    logic: "Shown to every profile at year-end and preserved as a separate historical instance each year.",
    documents: ["Current CH/OS export", "Upcoming renewal dates"],
    steps: [
      { id: "archive", title: "Archive the year’s important confirmations" },
      { id: "export", title: "Export a fresh CH/OS backup" },
      { id: "dates", title: "Add next year’s known deadlines" },
      { id: "assumptions", title: "Refresh profile assumptions and modules" },
    ],
    sources: [
      source("Swiss public information portal", "ch.ch", "https://www.ch.ch/en/"),
    ],
    legacyIds: ["yearly_life_review"],
  },
];

export const rules: RuleDefinition[] = baseRules.map((rule) => ({
  ...rule,
  contentVersion: contentPackVersion,
}));
